import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Withdrawal {
  id: string;
  withdrawal_date: string;
  quantity_withdrawn: number;
  product_id: string;
  product_name: string;
  reason?: string;
}

interface WithdrawalsComparativeStatsProps {
  withdrawals: Withdrawal[];
  customColor?: string;
}

export const WithdrawalsComparativeStats: React.FC<WithdrawalsComparativeStatsProps> = ({
  withdrawals,
  customColor
}) => {
  const calculateStats = useMemo(() => {
    const now = new Date();
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

    // Période actuelle (dernier mois)
    const currentPeriodWithdrawals = withdrawals.filter(withdrawal => {
      const withdrawalDate = new Date(withdrawal.withdrawal_date);
      return withdrawalDate >= oneMonthAgo && withdrawalDate <= now;
    });

    // 1 mois avant (2 mois à 1 mois avant)
    const oneMonthBeforeWithdrawals = withdrawals.filter(withdrawal => {
      const withdrawalDate = new Date(withdrawal.withdrawal_date);
      const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
      return withdrawalDate >= twoMonthsAgo && withdrawalDate < oneMonthAgo;
    });

    // 6 mois avant (7 mois à 6 mois avant)
    const sixMonthsBeforeWithdrawals = withdrawals.filter(withdrawal => {
      const withdrawalDate = new Date(withdrawal.withdrawal_date);
      const sevenMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 7, now.getDate());
      return withdrawalDate >= sevenMonthsAgo && withdrawalDate < sixMonthsAgo;
    });

    // 1 an avant (13 mois à 12 mois avant)
    const oneYearBeforeWithdrawals = withdrawals.filter(withdrawal => {
      const withdrawalDate = new Date(withdrawal.withdrawal_date);
      const thirteenMonthsAgo = new Date(now.getFullYear() - 1, now.getMonth() - 1, now.getDate());
      return withdrawalDate >= thirteenMonthsAgo && withdrawalDate < oneYearAgo;
    });

    const calculatePeriodStats = (periodWithdrawals: Withdrawal[]) => ({
      totalWithdrawals: periodWithdrawals.length,
      totalItems: periodWithdrawals.reduce((sum, withdrawal) => sum + withdrawal.quantity_withdrawn, 0),
      avgWithdrawalSize: periodWithdrawals.length > 0 ? periodWithdrawals.reduce((sum, withdrawal) => sum + withdrawal.quantity_withdrawn, 0) / periodWithdrawals.length : 0,
      uniqueProducts: new Set(periodWithdrawals.map(withdrawal => withdrawal.product_id)).size,
      // Calculer les raisons les plus fréquentes
      mostCommonReason: periodWithdrawals.length > 0 ? 
        Object.entries(periodWithdrawals.reduce((acc, w) => {
          const reason = w.reason || 'Non spécifiée';
          acc[reason] = (acc[reason] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)).sort(([,a], [,b]) => b - a)[0]?.[0] || 'Non spécifiée' : 'Aucune'
    });

    const current = calculatePeriodStats(currentPeriodWithdrawals);
    const oneMonthBefore = calculatePeriodStats(oneMonthBeforeWithdrawals);
    const sixMonthsBefore = calculatePeriodStats(sixMonthsBeforeWithdrawals);
    const oneYearBefore = calculatePeriodStats(oneYearBeforeWithdrawals);

    const calculateChange = (current: number, previous: number) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return (current - previous) / previous * 100;
    };

    return {
      current,
      comparisons: [{
        period: '1 mois avant',
        withdrawals: oneMonthBefore.totalWithdrawals,
        items: oneMonthBefore.totalItems,
        avgSize: oneMonthBefore.avgWithdrawalSize,
        products: oneMonthBefore.uniqueProducts,
        reason: oneMonthBefore.mostCommonReason,
        withdrawalsChange: calculateChange(current.totalWithdrawals, oneMonthBefore.totalWithdrawals),
        itemsChange: calculateChange(current.totalItems, oneMonthBefore.totalItems),
        avgSizeChange: calculateChange(current.avgWithdrawalSize, oneMonthBefore.avgWithdrawalSize),
        productsChange: calculateChange(current.uniqueProducts, oneMonthBefore.uniqueProducts)
      }, {
        period: '6 mois avant',
        withdrawals: sixMonthsBefore.totalWithdrawals,
        items: sixMonthsBefore.totalItems,
        avgSize: sixMonthsBefore.avgWithdrawalSize,
        products: sixMonthsBefore.uniqueProducts,
        reason: sixMonthsBefore.mostCommonReason,
        withdrawalsChange: calculateChange(current.totalWithdrawals, sixMonthsBefore.totalWithdrawals),
        itemsChange: calculateChange(current.totalItems, sixMonthsBefore.totalItems),
        avgSizeChange: calculateChange(current.avgWithdrawalSize, sixMonthsBefore.avgWithdrawalSize),
        productsChange: calculateChange(current.uniqueProducts, sixMonthsBefore.uniqueProducts)
      }, {
        period: '1 an avant',
        withdrawals: oneYearBefore.totalWithdrawals,
        items: oneYearBefore.totalItems,
        avgSize: oneYearBefore.avgWithdrawalSize,
        products: oneYearBefore.uniqueProducts,
        reason: oneYearBefore.mostCommonReason,
        withdrawalsChange: calculateChange(current.totalWithdrawals, oneYearBefore.totalWithdrawals),
        itemsChange: calculateChange(current.totalItems, oneYearBefore.totalItems),
        avgSizeChange: calculateChange(current.avgWithdrawalSize, oneYearBefore.avgWithdrawalSize),
        productsChange: calculateChange(current.uniqueProducts, oneYearBefore.uniqueProducts)
      }]
    };
  }, [withdrawals]);

  const getChangeIcon = (change: number) => {
    // Pour les retraits, une augmentation peut être négative (plus de pertes)
    if (change > 0) return <TrendingUp className="w-4 h-4 text-red-600" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-green-600" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  const getChangeColor = (change: number) => {
    // Pour les retraits, une diminution est positive (moins de pertes)
    if (change > 0) return 'text-red-600';
    if (change < 0) return 'text-green-600';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">Statistiques comparatives des retraits (dernier mois)</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {calculateStats.comparisons.map((comparison) => (
          <Card key={comparison.period} className="p-4">
            <CardContent className="p-0">
              <h4 className="font-medium text-muted-foreground mb-3">{comparison.period}</h4>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Nombre de retraits</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.withdrawalsChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.withdrawalsChange))}>
                      {comparison.withdrawalsChange > 0 ? '+' : ''}{comparison.withdrawalsChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {calculateStats.current.totalWithdrawals} | Avant: {comparison.withdrawals}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Articles retirés</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.itemsChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.itemsChange))}>
                      {comparison.itemsChange > 0 ? '+' : ''}{comparison.itemsChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {calculateStats.current.totalItems} | Avant: {comparison.items}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Produits différents</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.productsChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.productsChange))}>
                      {comparison.productsChange > 0 ? '+' : ''}{comparison.productsChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {calculateStats.current.uniqueProducts} | Avant: {comparison.products}
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Retrait moyen</span>
                  <div className="flex items-center gap-2">
                    {getChangeIcon(comparison.avgSizeChange)}
                    <span className={cn("text-sm font-medium", getChangeColor(comparison.avgSizeChange))}>
                      {comparison.avgSizeChange > 0 ? '+' : ''}{comparison.avgSizeChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Actuel: {calculateStats.current.avgWithdrawalSize.toFixed(1)} | Avant: {comparison.avgSize.toFixed(1)}
                </div>

                <div className="pt-2 border-t">
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">Raison principale:</span>
                  </div>
                  <div className="text-xs">
                    Actuel: {calculateStats.current.mostCommonReason}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Avant: {comparison.reason}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};